import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Spinner,
  Switch,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import {
  addDoc,
  collection,
  collectionGroup,
  doc,
  getDocs,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import PageShell from "../layout/PageShell";
import VersionManagementGuide from "./VersionManagementGuide";
import {
  certificationStatusOf,
  compareVersionNumbers,
  DEFAULT_PRODUCT_VERSION_FIELDS,
  emptyProductVersion,
  isCertifiedVersion,
  isRecordLocked,
  isVersionActive,
  isVersionIdentifier,
  parentVersionOf,
  PRODUCT_VERSIONS_COLLECTION,
  ProductVersion,
  productVersionLabel,
} from "../../productVersions/types";

const cardProps = {
  bg: "white",
  border: "1px solid",
  borderColor: "#d5e4ea",
  borderRadius: "lg",
  p: 5,
};

type Draft = Omit<ProductVersion, "id">;

type WorkstationRow = {
  storeId: string;
  storeName: string;
  stationName: string;
  installedVersion: string;
  productVersionId: string;
};

function versionKey(version: { idsev?: string; idversi?: string; version?: string }) {
  return `${version.idsev || ""}|${version.idversi || ""}|${version.version || ""}`;
}

function lockedLabel(value: any): string {
  if (!value) return "";
  const date = value.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
}

function latestCertified(versions: ProductVersion[]): ProductVersion | undefined {
  return versions
    .filter((item) => isVersionActive(item) && isCertifiedVersion(item))
    .sort((a, b) => compareVersionNumbers(b.version || "", a.version || ""))[0];
}

function RareVersionActions({ open, onToggle, isDefault, active, onDefault, onActive, onCertify, onLock }: {
  open: boolean;
  onToggle: () => void;
  isDefault: boolean;
  active: boolean;
  onDefault: () => void;
  onActive: () => void;
  onCertify?: () => void;
  onLock?: () => void;
}) {
  return (
    <Box position="absolute" top={3} right={3} zIndex={1} textAlign="right">
      <Text as="button" type="button" fontSize="20px" lineHeight="1" color="gray.500" aria-label="More" px={2} py={1} onClick={onToggle}>⋮</Text>
      {open ? (
        <VStack align="stretch" mt={1} spacing={1} bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={2} minW="148px">
          {onCertify ? <Text as="button" type="button" fontSize="sm" color="gray.600" textAlign="left" onClick={onCertify}>Certify</Text> : null}
          {onLock ? <Text as="button" type="button" fontSize="sm" color="gray.600" textAlign="left" onClick={onLock}>Lock</Text> : null}
          {!isDefault ? (
            <Text as="button" type="button" fontSize="sm" color="gray.600" textAlign="left" onClick={onDefault}>Set default</Text>
          ) : null}
          <Text as="button" type="button" fontSize="sm" color="gray.600" textAlign="left" onClick={onActive}>{active ? "Deactivate" : "Activate"}</Text>
        </VStack>
      ) : null}
    </Box>
  );
}

const ProductVersionsPage: React.FC = () => {
  const toast = useToast();
  const [versions, setVersions] = useState<ProductVersion[]>([]);
  const [workstations, setWorkstations] = useState<WorkstationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [copyFromId, setCopyFromId] = useState("");
  const [draft, setDraft] = useState<Draft>(emptyProductVersion());
  const [pendingActive, setPendingActive] = useState<{ id: string; next: boolean } | null>(null);
  const [unlockWord, setUnlockWord] = useState("");
  const [viewing, setViewing] = useState<ProductVersion | null>(null);
  const [editing, setEditing] = useState<ProductVersion | null>(null);
  const [editIdversi, setEditIdversi] = useState("");
  const [editParentVersion, setEditParentVersion] = useState("");
  const [editParentIdversi, setEditParentIdversi] = useState("");
  const [editCodcertif, setEditCodcertif] = useState("");
  const [installOpen, setInstallOpen] = useState(false);
  const [lockTarget, setLockTarget] = useState<ProductVersion | null>(null);
  const [lockWord, setLockWord] = useState("");
  const [rareOpen, setRareOpen] = useState(false);
  const [certifying, setCertifying] = useState(false);
  const [certifyWord, setCertifyWord] = useState("");
  const [editNameEn, setEditNameEn] = useState("");
  const [editNameFr, setEditNameFr] = useState("");
  const [editVersion, setEditVersion] = useState("");
  const [editOperatingMode, setEditOperatingMode] = useState("");
  const [editIdsev, setEditIdsev] = useState("");
  const [editIdpartn, setEditIdpartn] = useState("");
  const [editActivity, setEditActivity] = useState("");
  const [editSector, setEditSector] = useState("");
  const [tabIndex, setTabIndex] = useState(0);

  const storesUsingVersion = async (version: ProductVersion) => {
    const snap = await getDocs(collection(db, "stores"));
    return snap.docs.filter((store) => String(store.get("productVersionId") || "") === version.id).length;
  };

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, PRODUCT_VERSIONS_COLLECTION));
      let next = snap.docs.map((item) => ({ id: item.id, ...(item.data() as Omit<ProductVersion, "id">) }));
      if (next.length === 0) {
        const created = await addDoc(collection(db, PRODUCT_VERSIONS_COLLECTION), {
          ...DEFAULT_PRODUCT_VERSION_FIELDS,
          lockedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        next = [{ id: created.id, ...DEFAULT_PRODUCT_VERSION_FIELDS }];
      }

      const corrections = next.filter((item) => !isRecordLocked(item) && item.certificationStatus === "certified" && !item.certifiedAt);
      if (corrections.length) {
        const correctedParent = (item: ProductVersion) => (
          item.version === "6.6.2" && item.parentVersion === "6.6.2" ? "6.6.1" : (item.parentVersion || item.version)
        );
        await Promise.all(corrections.map((item) => {
          const parentVersion = correctedParent(item);
          const parentDoc = next.find((row) => row.version === parentVersion);
          return updateDoc(doc(db, PRODUCT_VERSIONS_COLLECTION, item.id), {
            certificationStatus: "pending",
            parentVersion,
            parentIdversi: item.parentIdversi || parentDoc?.idversi || "",
            locked: false,
            lockConfirmed: false,
            updatedAt: serverTimestamp(),
          });
        }));
        next = next.map((item) => {
          const correction = corrections.find((row) => row.id === item.id);
          if (!correction) return item;
          const parentVersion = correctedParent(item);
          const parentDoc = next.find((row) => row.version === parentVersion);
          return {
            ...item,
            certificationStatus: "pending" as const,
            parentVersion,
            parentIdversi: item.parentIdversi || parentDoc?.idversi || "",
            locked: false,
            lockConfirmed: false,
          };
        });
      }

      next.sort((a, b) => Number(b.isDefault) - Number(a.isDefault) || compareVersionNumbers(b.version || "", a.version || ""));
      const defaultVersion = next.find((item) => item.isDefault) || next[0];
      const stores = await getDocs(collection(db, "stores"));
      if (defaultVersion) {
        await Promise.all(
          stores.docs
            .filter((store) => !String(store.get("productVersionId") || ""))
            .map((store) => updateDoc(store.ref, { productVersionId: defaultVersion.id }))
        );
      }
      setVersions(next);
    } catch (error: any) {
      toast({ title: "Could not load versions", description: error.message, status: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setField = (key: keyof Draft, value: string | boolean) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const draftFrom = (source: ProductVersion) => {
    const { id: _id, createdAt: _created, updatedAt: _updated, lockedAt: _locked, certifiedAt: _certified, ...rest } = source;
    const parent = latestCertified(versions) || source;
    const parentNumber = isCertifiedVersion(source) ? source.version : (parent.parentVersion || parent.version);
    return {
      ...rest,
      version: "",
      parentVersion: parentNumber,
      parentIdversi: parent.idversi || source.parentIdversi || "",
      idversi: source.idversi,
      codcertif: "",
      certificationStatus: "pending" as const,
      isDefault: false,
      isActive: true,
      locked: false,
      lockConfirmed: false,
    };
  };

  const startCreate = () => {
    const source = latestCertified(versions) || versions[0];
    if (!source) {
      setDraft({ ...emptyProductVersion(), version: "", certificationStatus: "pending", locked: false, parentVersion: "" });
      setCopyFromId("");
      setFormOpen(true);
      setViewing(null);
      setTabIndex(0);
      return;
    }
    setCopyFromId(source.id);
    setDraft(draftFrom(source));
    setFormOpen(true);
    setViewing(null);
    setTabIndex(0);
  };

  const applyCopy = (sourceId: string) => {
    setCopyFromId(sourceId);
    const source = versions.find((item) => item.id === sourceId);
    if (!source) return;
    setDraft(draftFrom(source));
  };

  const save = async () => {
    const version = draft.version.trim();
    if (!draft.nameEn.trim() || !version || !draft.idsev.trim() || !draft.idversi.trim()) {
      toast({ title: "Name, version, product identifier, and version identifier are required", status: "error" });
      return;
    }
    if (!draft.parentVersion.trim()) {
      toast({ title: "Parent version is required", status: "error" });
      return;
    }
    if (versions.some((item) => item.version === version)) {
      toast({ title: "This version number already exists", status: "error" });
      return;
    }
    const key = versionKey({ ...draft, version });
    if (versions.some((item) => versionKey(item) === key)) {
      toast({
        title: "This version already exists",
        description: "Change the version or its identifiers.",
        status: "error",
      });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...draft,
        nameFr: draft.nameFr.trim() || draft.nameEn.trim(),
        nameEn: draft.nameEn.trim(),
        version,
        parentVersion: draft.parentVersion.trim(),
        certificationStatus: "pending" as const,
        idsev: draft.idsev.trim(),
        idversi: draft.idversi.trim(),
        idpartn: draft.idpartn.trim(),
        codcertif: draft.codcertif.trim(),
        parentIdversi: String(draft.parentIdversi || "").trim(),
        apprlinit: draft.apprlinit.trim() || "SEV",
        isActive: true,
        locked: false,
        lockConfirmed: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      if (payload.isDefault) {
        const batch = writeBatch(db);
        versions.forEach((item) => {
          if (item.isDefault) batch.update(doc(db, PRODUCT_VERSIONS_COLLECTION, item.id), { isDefault: false });
        });
        await batch.commit();
      }
      await addDoc(collection(db, PRODUCT_VERSIONS_COLLECTION), payload);
      toast({ title: "Pending version saved", status: "success" });
      setFormOpen(false);
      await load();
    } catch (error: any) {
      toast({ title: "Could not save", description: error.message, status: "error" });
    } finally {
      setSaving(false);
    }
  };

  const setDefault = async (version: ProductVersion) => {
    if (isRecordLocked(version)) {
      toast({ title: "This version is locked", status: "error" });
      return;
    }
    if (!isVersionActive(version)) {
      toast({ title: "Activate this version before making it the default", status: "error" });
      return;
    }
    if (version.isDefault) return;
    setSaving(true);
    try {
      const batch = writeBatch(db);
      versions.forEach((item) => {
        if (item.isDefault) batch.update(doc(db, PRODUCT_VERSIONS_COLLECTION, item.id), { isDefault: false });
      });
      batch.update(doc(db, PRODUCT_VERSIONS_COLLECTION, version.id), { isDefault: true, updatedAt: serverTimestamp() });
      await batch.commit();
      toast({ title: "Default version updated", description: "New workstations use this version until they report an installed version.", status: "success" });
      await load();
    } catch (error: any) {
      toast({ title: "Could not update the default", description: error.message, status: "error" });
    } finally {
      setSaving(false);
    }
  };

  const askActiveChange = async (version: ProductVersion, next: boolean) => {
    if (isRecordLocked(version)) {
      toast({ title: "This version is locked", status: "error" });
      return;
    }
    const activeCount = versions.filter((item) => isVersionActive(item)).length;
    if (!next && activeCount <= 1) {
      toast({ title: "At least one version must stay active", status: "error" });
      return;
    }
    if (!next && version.isDefault) {
      toast({ title: "Set another active version as the default first", status: "error" });
      return;
    }
    if (!next) {
      const storeCount = await storesUsingVersion(version);
      const stationCount = workstations.filter((row) => row.productVersionId === version.id).length;
      if (storeCount > 0 || stationCount > 0) {
        toast({
          title: "This version is still assigned",
          description: "Move the stores and workstations to another version before turning it off.",
          status: "error",
          duration: 6000,
        });
        return;
      }
    }
    setUnlockWord("");
    setPendingActive({ id: version.id, next });
  };

  const confirmActiveChange = async () => {
    if (!pendingActive || unlockWord.trim() !== "unlock") return;
    setSaving(true);
    try {
      await updateDoc(doc(db, PRODUCT_VERSIONS_COLLECTION, pendingActive.id), {
        isActive: pendingActive.next,
        updatedAt: serverTimestamp(),
      });
      toast({
        title: pendingActive.next ? "Version activated" : "Version deactivated",
        description: "The version details were not changed.",
        status: "success",
      });
      setPendingActive(null);
      await load();
    } catch (error: any) {
      toast({ title: "Could not update the version", description: error.message, status: "error" });
    } finally {
      setSaving(false);
    }
  };

  const fillEdit = (version: ProductVersion, forCertify: boolean) => {
    if (isRecordLocked(version)) return;
    const parentProfile = versions.find((item) => item.version === parentVersionOf(version));
    setEditing(version);
    setCertifying(forCertify);
    setEditNameEn(version.nameEn || "");
    setEditNameFr(version.nameFr || "");
    setEditVersion(version.version || "");
    setEditOperatingMode(version.operatingMode || "");
    setEditIdsev(version.idsev || "");
    setEditIdpartn(version.idpartn || "");
    setEditActivity(version.activityField || "");
    setEditSector(version.sector || "");
    setEditIdversi(version.idversi || "");
    setEditParentVersion(forCertify ? (version.version || "") : parentVersionOf(version));
    setEditParentIdversi(forCertify ? (version.idversi || "") : (version.parentIdversi || parentProfile?.idversi || ""));
    setEditCodcertif(version.codcertif || "");
    setCertifyWord("");
    setFormOpen(false);
    setViewing(null);
    setInstallOpen(false);
    setRareOpen(false);
  };

  const openPending = (version: ProductVersion) => {
    fillEdit(version, false);
  };

  const savePendingFields = async () => {
    if (!editing || isRecordLocked(editing)) return;
    const idversi = editIdversi.trim();
    const parentIdversi = editParentIdversi.trim();
    const parentVersion = editParentVersion.trim();
    const codcertif = editCodcertif.trim();
    if (!isVersionIdentifier(idversi) || !isVersionIdentifier(parentIdversi)) {
      toast({ title: "Version identifiers are 16 hexadecimal characters", status: "error" });
      return;
    }
    if (!parentVersion) {
      toast({ title: "Parent version is required", status: "error" });
      return;
    }
    const key = versionKey({ ...editing, idversi });
    if (versions.some((item) => item.id !== editing.id && versionKey(item) === key)) {
      toast({ title: "Another version already uses this identifier", status: "error" });
      return;
    }
    setSaving(true);
    try {
      await updateDoc(doc(db, PRODUCT_VERSIONS_COLLECTION, editing.id), {
        idversi,
        parentVersion,
        parentIdversi,
        codcertif,
        updatedAt: serverTimestamp(),
      });
      toast({ title: "Version updated", status: "success" });
      setEditing(null);
      await load();
    } catch (error: any) {
      toast({ title: "Could not update the version", description: error.message, status: "error" });
    } finally {
      setSaving(false);
    }
  };

  const confirmLock = async () => {
    if (!lockTarget || lockWord.trim() !== "lock" || isRecordLocked(lockTarget)) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, PRODUCT_VERSIONS_COLLECTION, lockTarget.id), {
        locked: true,
        lockConfirmed: true,
        lockedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      toast({ title: "Version locked", description: "These details can no longer be changed.", status: "success" });
      setLockTarget(null);
      setEditing(null);
      await load();
    } catch (error: any) {
      toast({ title: "Could not lock the version", description: error.message, status: "error" });
    } finally {
      setSaving(false);
    }
  };

  const saveCertified = async () => {
    if (!editing || isRecordLocked(editing)) return;
    const version = editVersion.trim();
    const idversi = editIdversi.trim();
    const parentVersion = editParentVersion.trim();
    const parentIdversi = editParentIdversi.trim();
    const codcertif = editCodcertif.trim();
    const idsev = editIdsev.trim();
    if (!version || !idsev || !isVersionIdentifier(idversi) || !isVersionIdentifier(parentIdversi) || !parentVersion || !codcertif) {
      toast({ title: "Enter the version, identifiers, parent version, and certificate code", status: "error" });
      return;
    }
    if (certifyWord.trim() !== "certify") return;
    if (versions.some((item) => item.id !== editing.id && item.version === version)) {
      toast({ title: "This version number already exists", status: "error" });
      return;
    }
    setSaving(true);
    try {
      await updateDoc(doc(db, PRODUCT_VERSIONS_COLLECTION, editing.id), {
        nameEn: editNameEn.trim(),
        nameFr: editNameFr.trim() || editNameEn.trim(),
        version,
        operatingMode: editOperatingMode.trim(),
        idsev,
        idpartn: editIdpartn.trim(),
        activityField: editActivity.trim(),
        sector: editSector.trim(),
        idversi,
        parentVersion,
        parentIdversi,
        codcertif,
        certificationStatus: "certified",
        certifiedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      toast({ title: "Version certified", status: "success" });
      setCertifying(false);
      setCertifyWord("");
      setEditing(null);
      await load();
    } catch (error: any) {
      toast({ title: "Could not certify the version", description: error.message, status: "error" });
    } finally {
      setSaving(false);
    }
  };

  const closeForms = () => {
    setFormOpen(false);
    setViewing(null);
    setEditing(null);
    setInstallOpen(false);
    setRareOpen(false);
    setCertifying(false);
    setCertifyWord("");
  };

  const openInstallList = async () => {
    setInstallOpen(true);
    setFormOpen(false);
    setViewing(null);
    setEditing(null);
    setTabIndex(0);
    if (workstations.length) return;
    setLoading(true);
    try {
      const stores = await getDocs(collection(db, "stores"));
      const storeName = new Map(stores.docs.map((store) => [store.id, String(store.get("name") || store.id)]));
      const storeVersion = new Map(stores.docs.map((store) => [store.id, String(store.get("productVersionId") || "")]));
      const stationSnap = await getDocs(collectionGroup(db, "stations"));
      const rows = stationSnap.docs
        .filter((item) => item.ref.parent.id === "stations")
        .map((item) => {
          const data = item.data() as { stationName?: string; installedVersion?: string; productVersionId?: string };
          const storeId = item.ref.parent.parent?.id || "";
          return {
            storeId,
            storeName: storeName.get(storeId) || storeId,
            stationName: String(data.stationName || "Workstation"),
            installedVersion: String(data.installedVersion || ""),
            productVersionId: String(data.productVersionId || storeVersion.get(storeId) || ""),
          };
        })
        .filter((row) => row.storeId)
        .sort((a, b) => a.storeName.localeCompare(b.storeName) || a.stationName.localeCompare(b.stationName));
      setWorkstations(rows);
    } catch (error: any) {
      toast({ title: "Could not load the install list", description: error.message, status: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell
      title="Version"
      description="Software versions for billing. A new workstation uses the store default until it reports the version it has installed."
      actions={
        formOpen || viewing || editing || installOpen ? (
          <Button variant="outline" onClick={closeForms}>Back to list</Button>
        ) : (
          <HStack>
            <Button variant="outline" onClick={openInstallList}>View install list</Button>
            <Button onClick={startCreate}>New version</Button>
          </HStack>
        )
      }
    >
      <Tabs colorScheme="blue" variant="enclosed" index={tabIndex} onChange={setTabIndex}>
        <TabList mb={4}>
          <Tab>Version</Tab>
          <Tab>Version management</Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0}>
            {loading ? (
              <Spinner />
            ) : formOpen ? (
              <Box {...cardProps}>
                <Text fontWeight="600" mb={1}>New version</Text>
                <Text fontSize="sm" color="gray.600" mb={4}>
                  The new version stays pending. Workstations that install it send the new version and the certified parent below. The version identifier and certificate code stay with the parent until you replace them.
                </Text>
                <VStack align="stretch" spacing={4}>
                  <FormControl>
                    <FormLabel>Copy details from</FormLabel>
                    <Select value={copyFromId} onChange={(event) => applyCopy(event.target.value)}>
                      {versions.map((version) => (
                        <option key={version.id} value={version.id}>{productVersionLabel(version)}</option>
                      ))}
                    </Select>
                  </FormControl>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl isRequired>
                      <FormLabel>English name</FormLabel>
                      <Input value={draft.nameEn} onChange={(event) => setField("nameEn", event.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>French name</FormLabel>
                      <Input value={draft.nameFr} onChange={(event) => setField("nameFr", event.target.value)} />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>Version</FormLabel>
                      <Input value={draft.version} onChange={(event) => setField("version", event.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Parent version</FormLabel>
                      <Input value={draft.parentVersion} onChange={(event) => setField("parentVersion", event.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Parent version identifier</FormLabel>
                      <Input value={draft.parentIdversi || ""} onChange={(event) => setField("parentIdversi", event.target.value)} />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>Version identifier</FormLabel>
                      <Input value={draft.idversi} onChange={(event) => setField("idversi", event.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Operating mode</FormLabel>
                      <Input value={draft.operatingMode} onChange={(event) => setField("operatingMode", event.target.value)} />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>Product identifier</FormLabel>
                      <Input value={draft.idsev} onChange={(event) => setField("idsev", event.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Partner identifier</FormLabel>
                      <Input value={draft.idpartn} onChange={(event) => setField("idpartn", event.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Certificate code</FormLabel>
                      <Input value={draft.codcertif} onChange={(event) => setField("codcertif", event.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Field of activity</FormLabel>
                      <Input value={draft.activityField} onChange={(event) => setField("activityField", event.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Sector</FormLabel>
                      <Input value={draft.sector} onChange={(event) => setField("sector", event.target.value)} />
                    </FormControl>
                  </SimpleGrid>
                  <FormControl display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <FormLabel mb={0}>Default for new workstations</FormLabel>
                      <Text fontSize="sm" color="gray.600">A workstation uses this version until it reports the version it has installed.</Text>
                    </Box>
                    <Switch isChecked={draft.isDefault} onChange={(event) => setField("isDefault", event.target.checked)} />
                  </FormControl>
                  <Button alignSelf="flex-start" onClick={save} isLoading={saving}>Save pending version</Button>
                </VStack>
              </Box>
            ) : editing ? (
              <Box {...cardProps} position="relative">
                <Text fontWeight="600" mb={1} pr={10}>{certifying ? "Certify this version" : productVersionLabel(editing)}</Text>
                <Text fontSize="sm" color="gray.600" mb={4}>
                  {certifying
                    ? "Parent version starts as this version. Change the certificate code and any other field. Type certify to finish."
                    : `Current version ${editing.version}. Add the certificate code when Revenu Québec sends it.`}
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel>English name</FormLabel>
                    <Input value={certifying ? editNameEn : (editing.nameEn || "")} isReadOnly={!certifying} bg={certifying ? "white" : "gray.50"} onChange={(event) => setEditNameEn(event.target.value)} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>French name</FormLabel>
                    <Input value={certifying ? editNameFr : (editing.nameFr || "")} isReadOnly={!certifying} bg={certifying ? "white" : "gray.50"} onChange={(event) => setEditNameFr(event.target.value)} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Version</FormLabel>
                    <Input value={certifying ? editVersion : (editing.version || "")} isReadOnly={!certifying} bg={certifying ? "white" : "gray.50"} onChange={(event) => setEditVersion(event.target.value)} />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Parent version</FormLabel>
                    <Input value={editParentVersion} onChange={(event) => setEditParentVersion(event.target.value)} />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Version identifier</FormLabel>
                    <Input value={editIdversi} onChange={(event) => setEditIdversi(event.target.value)} />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Parent version identifier</FormLabel>
                    <Input value={editParentIdversi} onChange={(event) => setEditParentIdversi(event.target.value)} />
                  </FormControl>
                  <FormControl isRequired={certifying}>
                    <FormLabel>Certificate code</FormLabel>
                    <Input value={editCodcertif} onChange={(event) => setEditCodcertif(event.target.value)} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Operating mode</FormLabel>
                    <Input value={certifying ? editOperatingMode : (editing.operatingMode || "")} isReadOnly={!certifying} bg={certifying ? "white" : "gray.50"} onChange={(event) => setEditOperatingMode(event.target.value)} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Product identifier</FormLabel>
                    <Input value={certifying ? editIdsev : (editing.idsev || "")} isReadOnly={!certifying} bg={certifying ? "white" : "gray.50"} onChange={(event) => setEditIdsev(event.target.value)} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Partner identifier</FormLabel>
                    <Input value={certifying ? editIdpartn : (editing.idpartn || "")} isReadOnly={!certifying} bg={certifying ? "white" : "gray.50"} onChange={(event) => setEditIdpartn(event.target.value)} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Field of activity</FormLabel>
                    <Input value={certifying ? editActivity : (editing.activityField || "")} isReadOnly={!certifying} bg={certifying ? "white" : "gray.50"} onChange={(event) => setEditActivity(event.target.value)} />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Sector</FormLabel>
                    <Input value={certifying ? editSector : (editing.sector || "")} isReadOnly={!certifying} bg={certifying ? "white" : "gray.50"} onChange={(event) => setEditSector(event.target.value)} />
                  </FormControl>
                </SimpleGrid>
                {certifying ? (
                  <FormControl mt={4} isRequired>
                    <FormLabel>Type certify</FormLabel>
                    <Input value={certifyWord} onChange={(event) => setCertifyWord(event.target.value)} placeholder="certify" autoComplete="off" />
                  </FormControl>
                ) : null}
                <Button mt={4} onClick={certifying ? saveCertified : savePendingFields} isLoading={saving} isDisabled={certifying && certifyWord.trim() !== "certify"}>{certifying ? "Certify" : "Save"}</Button>
                <RareVersionActions
                  open={rareOpen}
                  onToggle={() => setRareOpen((value) => !value)}
                  isDefault={editing.isDefault}
                  active={isVersionActive(editing)}
                  onDefault={() => setDefault(editing)}
                  onActive={() => askActiveChange(editing, !isVersionActive(editing))}
                  onCertify={certifying || isCertifiedVersion(editing) ? undefined : () => fillEdit(editing, true)}
                  onLock={() => { setLockWord(""); setLockTarget(editing); }}
                />
              </Box>
            ) : viewing ? (
              <Box {...cardProps} position="relative">
                <Text fontWeight="600" mb={1} pr={10}>{productVersionLabel(viewing)}</Text>
                <Text fontSize="sm" color="gray.600" mb={4}>
                  {isRecordLocked(viewing) ? "This version is locked." : "This version is not certified. Update it to add the certificate code."}
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  {([
                    ["English name", viewing.nameEn],
                    ["French name", viewing.nameFr],
                    ["Version", viewing.version],
                    ["Parent version", parentVersionOf(viewing)],
                    ["Parent version identifier", viewing.parentIdversi || ""],
                    ["Certification", certificationStatusOf(viewing) === "certified" ? "Certified" : "Not certified"],
                    ["Version identifier", viewing.idversi],
                    ["Operating mode", viewing.operatingMode],
                    ["Product identifier", viewing.idsev],
                    ["Partner identifier", viewing.idpartn],
                    ["Certificate code", viewing.codcertif],
                    ["Field of activity", viewing.activityField],
                    ["Sector", viewing.sector],
                    ["Document ID", viewing.id],
                  ] as [string, string][]).map(([label, value]) => (
                    <FormControl key={label}>
                      <FormLabel>{label}</FormLabel>
                      <Input value={value || ""} isReadOnly bg="gray.50" />
                    </FormControl>
                  ))}
                </SimpleGrid>
                <RareVersionActions
                  open={rareOpen}
                  onToggle={() => setRareOpen((value) => !value)}
                  isDefault={viewing.isDefault}
                  active={isVersionActive(viewing)}
                  onDefault={() => setDefault(viewing)}
                  onActive={() => askActiveChange(viewing, !isVersionActive(viewing))}
                  onCertify={isRecordLocked(viewing) || isCertifiedVersion(viewing) ? undefined : () => fillEdit(viewing, true)}
                />
              </Box>
            ) : installOpen ? (
              <Box {...cardProps}>
                <Text fontWeight="600" mb={1}>Install list</Text>
                <Text fontSize="sm" color="gray.600" mb={4}>Workstations and the version installed on each one.</Text>
                {workstations.length === 0 ? (
                  <Text fontSize="sm">No workstations reported.</Text>
                ) : (
                  <VStack align="stretch" spacing={2}>
                    {workstations.map((row) => {
                      const profile = versions.find((item) => item.id === row.productVersionId);
                      const mismatch = Boolean(row.installedVersion) && (!profile || profile.version !== row.installedVersion);
                      return (
                        <Text key={`${row.storeId}-${row.stationName}`} fontSize="sm">
                          {row.storeName} · {row.stationName} · installed {row.installedVersion || "not reported"}
                          {profile ? ` · ${productVersionLabel(profile)}` : ""}
                          {mismatch ? " · mismatch" : ""}
                        </Text>
                      );
                    })}
                  </VStack>
                )}
              </Box>
            ) : (
              <VStack align="stretch" spacing={3}>
                {versions.map((version) => {
                  const active = isVersionActive(version);
                  const certified = isCertifiedVersion(version);
                  const locked = isRecordLocked(version);
                  const lockedOn = lockedLabel(version.lockedAt);
                  return (
                    <Box key={version.id} {...cardProps}>
                      <HStack justify="space-between" align="start" spacing={4}>
                        <Box minW={0}>
                          <Text fontWeight="600" noOfLines={1}>{productVersionLabel(version)}</Text>
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={1} mt={2}>
                            <Text fontSize="sm" color="gray.600">French name {version.nameFr || ""}</Text>
                            <Text fontSize="sm" color="gray.600">Parent version {parentVersionOf(version)}</Text>
                            <Text fontSize="sm" color="gray.600">Version identifier {version.idversi}</Text>
                            <Text fontSize="sm" color="gray.600">Parent version identifier {version.parentIdversi || versions.find((item) => item.version === parentVersionOf(version))?.idversi || ""}</Text>
                            <Text fontSize="sm" color="gray.600">Product identifier {version.idsev}</Text>
                            <Text fontSize="sm" color="gray.600">Partner identifier {version.idpartn}</Text>
                            <Text fontSize="sm" color="gray.600">Certificate code {version.codcertif || ""}</Text>
                            <Text fontSize="sm" color="gray.600">Operating mode {version.operatingMode}</Text>
                            <Text fontSize="sm" color="gray.600">Field of activity {version.activityField}</Text>
                            <Text fontSize="sm" color="gray.600">Sector {version.sector}</Text>
                          </SimpleGrid>
                          <HStack mt={2} spacing={2} flexWrap="wrap">
                            <Badge colorScheme={certified ? "green" : "orange"}>{certified ? "Certified" : "Not certified"}</Badge>
                            {locked ? <Badge>Locked{lockedOn ? ` ${lockedOn}` : ""}</Badge> : null}
                            {version.isDefault ? <Badge colorScheme="green">Default</Badge> : null}
                            <Badge colorScheme={active ? "green" : "gray"}>{active ? "Active" : "Inactive"}</Badge>
                          </HStack>
                        </Box>
                        <VStack align="stretch" spacing={2} flexShrink={0}>
                          {locked ? (
                            <Button size="sm" variant="outline" onClick={() => { setRareOpen(false); closeForms(); setViewing(version); }}>View</Button>
                          ) : (
                            <Button size="sm" variant="outline" onClick={() => openPending(version)}>Update</Button>
                          )}
                        </VStack>
                      </HStack>
                    </Box>
                  );
                })}
              </VStack>
            )}
          </TabPanel>
          <TabPanel px={0}>
            <VersionManagementGuide />
          </TabPanel>
        </TabPanels>
      </Tabs>

      <Modal isOpen={pendingActive !== null} onClose={() => setPendingActive(null)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{pendingActive?.next ? "Activate version" : "Deactivate version"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <AlertDescription>
                  This does not change the version details. At least one version stays active. New workstations keep using the default version until they report an installed version.
                </AlertDescription>
              </Alert>
              <FormControl>
                <FormLabel>Type unlock to continue</FormLabel>
                <Input value={unlockWord} onChange={(event) => setUnlockWord(event.target.value)} placeholder="unlock" autoComplete="off" />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setPendingActive(null)}>Cancel</Button>
            <Button colorScheme="red" isDisabled={unlockWord.trim() !== "unlock"} isLoading={saving} onClick={confirmActiveChange}>
              {pendingActive?.next ? "Activate" : "Deactivate"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={lockTarget !== null} onClose={() => setLockTarget(null)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Lock version</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Text fontSize="sm">
                After this version is locked, its version, parent version, identifiers, and certificate code stay as they are.
              </Text>
              <FormControl>
                <FormLabel>Type lock to continue</FormLabel>
                <Input value={lockWord} onChange={(event) => setLockWord(event.target.value)} autoComplete="off" />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setLockTarget(null)}>Cancel</Button>
            <Button isDisabled={lockWord.trim() !== "lock"} isLoading={saving} onClick={confirmLock}>Lock</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageShell>
  );
};

export default ProductVersionsPage;
